export const projects = [
  {
    id: "tempa-digital",
    title: "Tempa Digital – Design & Development Studio",
    description: "A boutique design and development studio crafting digital products, pipelines, and platforms for ambitious teams — with a focus on system thinking, interaction design, and full-stack engineering.",
    fullDescription: `
## Project Overview

Tempa Digital is my design and development studio — a living brand, technical playground, and client-facing presence all in one. Built from the ground up as a Next.js 16 application with Three.js, React Three Fiber, and custom post-processing effects, the site serves as both a showcase of what I build and *how* I think about building.

Every surface, transition, and interaction on the site is authored — from the 3D scene rigging to the typographic scale, from the capability strip to the system detail panels. The studio reflects the same principles I bring to client work: opinionated design, performance-first engineering, and a relentless focus on craft.

## Key Features

🎨 **Immersive 3D Experience**: Custom Three.js scene with animated artifacts, dynamic flight paths, and orbital camera rigging using React Three Fiber and post-processing layers.

🧩 **Capability Strip**: An interactive taxonomy of services — booking systems, gated content, regional platforms, routing, search, and signal pipelines — each with live tokens and dynamic category panels.

📋 **Case Studies**: In-depth project breakdowns (Gunung, SAPOT, Clinical Decision Support) with scrub-based sequences that let visitors explore the technical depth of past work.

⚙️ **System Page**: A dedicated token-based control panel and system overview that lays out the studio's technical architecture and design system thinking.

🎚️ **Flight Section & Sequence Components**: Custom scrubbable, animated narratives that reveal project details through scroll-driven interactions — static summaries, scrubbable timelines, and full-bleed deep dives.

🌗 **Theme System**: Programmatic light/dark mode with persistent preferences, smooth CSS transitions, and full palette generation from a constrained color token set.

## Technical Highlights

✔ **Next.js 16 with React 19**: App Router, TypeScript, server components, and static export for maximum performance and deployment flexibility.

✔ **React Three Fiber**: Custom 3D scenes with artifact geometry, station placement, procedural path generation, and orbital camera controls — all declarative within the React tree.

✔ **Motion (Framer Motion)**: Scroll-driven animations, page transitions, layout animations, and gesture-aware interactions throughout the UI.

✔ **Design System**: Token-based colors, typographic scales, spacing rhythms — all driven from a single source of truth and exposed through live preview panels.

✔ **Post-processing Pipeline**: Bloom, vignette, chromatic aberration, and custom effects applied through react-three/postprocessing for a cinematic feel.

✔ **Performance Engineering**: Static export, optimized Three.js geometry, code splitting, and careful GPU budget management throughout all 3D layers.

✔ **Typography**: Custom type specs with responsive scaling, fluid line heights, and deliberate hierarchy — treated as a first-class design material.

## Technologies Used

🔹 **Frontend**: Next.js 16 | React 19 | TypeScript | Tailwind CSS 4 | Motion (Framer)

🔹 **3D**: React Three Fiber | Three.js | React Three Drei | React Three Postprocessing

🔹 **Styling**: CSS Modules | Custom Properties | Token-driven design system

🔹 **Deployment**: Vercel | Static Export

This project embodies my philosophy as a developer: the studio is the message. Every detail — from the orbital camera logic to the loading tokens animation — is a statement about what I value in digital craftsmanship.`,
    image: "/images/projects/tempa-digital.png",
    technologies: ["Next.js 16", "React 19", "TypeScript", "Three.js", "React Three Fiber", "Motion", "Tailwind CSS 4", "Postprocessing"],
    categories: ["Web & Software Development", "Brand & Design", "3D & Motion"],
    demoLink: "https://tempadigital.com",
  },
  {
    id: "gunung",
    title: "Gunung – Official Distribution & E-Commerce Platform for Climbing Gear",
    description: "Full-stack distribution and e-commerce platform connecting Malaysian climbers with international climbing brands through official partnerships, B2B distribution, and D2C online store with comprehensive admin dashboard.",
    fullDescription: `
## Project Overview

Gunung is a distribution and e-commerce platform connecting Malaysian climbers with quality climbing gear. The platform serves both business customers and individual climbers through partnerships with international climbing brands.

Built with modern web technologies, the platform features a customer-facing online store and backend admin systems for managing operations.

## Key Features

🛒 **E-Commerce Store**: Online product catalog with shopping cart and checkout functionality.

🎛️ **Admin Dashboard**: Comprehensive management system for inventory, pricing, and operations.

🏔️ **Brand Website**: Landing pages showcasing the brand story and mission.

## Technical Highlights

✔ **Modern Full-Stack Architecture**: Built with Next.js and React for optimal performance.

✔ **Type-Safe Development**: TypeScript ensures code reliability and maintainability.

✔ **Responsive Design**: Mobile-first approach optimized for all device sizes.

## Technologies Used

🔹 **Frontend**: Next.js | React | TypeScript | Tailwind CSS

🔹 **Backend**: PostgreSQL | Authentication & Security

🔹 **Payments**: Integrated payment processing

This project demonstrates full-stack e-commerce development, business platform design, and the ability to build scalable web applications that serve real business needs.`,
    image: "/images/projects/gunung.png",
    technologies: ["Next.js", "React", "TypeScript", "PostgreSQL", "Tailwind CSS"],
    categories: ["Web & Software Development", "E-commerce", "Brand & Design"],
    demoLink: "https://gunungclimbing.my/",
  },
  {
    id: "midpoint-maps",
    title: "MIDPOINT – Intelligent Meeting Point Finder",
    description: "A sophisticated web application that revolutionizes group meetup planning through collaborative workflows, real-time optimization algorithms, comprehensive place discovery, and enterprise-grade caching infrastructure that reduces API costs by 80-90% while delivering 38x faster response times.",
    fullDescription: `
## Project Overview

MIDPOINT is a comprehensive meeting coordination platform that goes far beyond simple midpoint calculation. The application features a complete collaborative workflow where groups can create shareable meetings, vote democratically on locations and times, communicate in real-time, and make informed decisions together. With support for both solo calculations and complex group dynamics, MIDPOINT handles everything from quick friend meetups to professional business gatherings with sophisticated optimization algorithms, intelligent place discovery, and a production-ready caching architecture designed to scale to millions of users cost-effectively.

## Key Features

🔐 **Complete User System**: Secure Supabase authentication with custom profiles, avatar uploads, session management, and guest access with usage limits.

🎯 **Advanced Solo Calculations**: Multi-algorithm optimization (distance-based, equal travel time, minimize total time) supporting 2-5 locations with real-time route visualization.

👥 **Collaborative Group Meetings**: Shareable meeting links with progressive workflow (setup → join → voting → finalization), real-time participant tracking, and flexible timing options.

🗳️ **Democratic Voting System**: Location and time slot voting with live vote counts, automatic workflow progression, and participant status tracking.

💬 **Real-time Communication**: In-meeting chat system with Supabase real-time subscriptions, participant awareness, and live message synchronization.

🗺️ **Advanced Mapping Interface**: Interactive Leaflet-powered maps with route visualization, multiple tile providers, accurate travel time display, and mobile-responsive design.

📍 **Intelligent Venue Discovery**: Multi-provider integration (Google Places, OpenStreetMap) with smart filtering (dietary preferences, religious considerations, shopping types), detailed venue information, and proximity optimization.

🚀 **Enterprise-Grade Caching**: 3-tier caching architecture (Redis → Database → API) with 1M POI capacity, click tracking, popularity scoring, and automatic LRU eviction reducing API costs by 80-90%.

📊 **Meeting Management**: Complete meeting history, status tracking, participant insights, and meeting analytics for authenticated users.

🛡️ **Usage Controls**: Distributed rate limiting with Redis persistence, anonymous user policies, usage tracking, and comprehensive performance optimization.

## Technical Highlights

✔ **Production-Ready Caching Architecture**: 3-tier system (Redis/Vercel KV → PostgreSQL → External APIs) with automatic failover and 6-hour cache duration

✔ **Database POI Caching**: PostGIS-powered geospatial database supporting 1M cached POIs with intelligent popularity scoring and LRU eviction strategy

✔ **Click Analytics**: Real-time user interaction tracking with weighted popularity algorithm: score = (clicks × 10) + (recency_bonus) + (rating × 5)

✔ **Cost Optimization**: Reduces API costs from $32k to $3.2-6.4k/month at 1M requests through intelligent caching and request deduplication

✔ **Performance Engineering**: 38x faster cached queries (50ms vs 1900ms), client-side persistent caching (24hr TTL), and profile caching (10min TTL)

✔ **Full-Stack Architecture**: Next.js 13+ with serverless API routes, React 18 with advanced state management, and custom service role authentication

✔ **Real-time Database**: Supabase PostgreSQL with live subscriptions, Row Level Security policies, PostGIS extensions, and custom SQL functions

✔ **Multi-API Integration**: OpenRouteService (routing/matrix), GraphHopper (backup routing), Google Places + OSM (venues with auto-fallback), Photon (geocoding)

✔ **Advanced Algorithms**: Multiple midpoint optimization strategies, geospatial radius queries, intelligent place scoring, and real-time synchronization

✔ **Responsive Design**: Mobile-first Tailwind CSS implementation with cross-device optimization and progressive enhancement

✔ **Security Implementation**: JWT tokens, service role segregation, input validation, CORS configuration, and Redis-backed distributed rate limiting

## Technologies Used

🔹 **Frontend**: Next.js | React 18 | Tailwind CSS | Leaflet | React Context API | localStorage caching

🔹 **Backend**: Next.js API Routes | Supabase | PostgreSQL + PostGIS | JWT Authentication | Service Role separation

🔹 **Caching Layer**: Redis (Vercel KV) | Database POI cache | Client-side localStorage | In-memory fallback

🔹 **Database**: PostgreSQL with PostGIS | GIN/GIST indexes | Custom SQL functions | Triggers & views | RLS policies

🔹 **External APIs**: OpenRouteService | GraphHopper | Google Places | OpenStreetMap | Photon | Nominatim

🔹 **Real-time**: Supabase Realtime | WebSocket connections | Live subscriptions | Optimistic updates

🔹 **Tools**: Axios with retry logic | Phosphor Icons | Custom Hooks | Diagnostic test suite

## Performance & Scalability

**Caching Architecture:**
- **Layer 1 (Redis)**: 50ms response, 6hr TTL, 40-60% hit rate
- **Layer 2 (Database)**: 200ms response, persistent storage, 30-40% hit rate
- **Layer 3 (External APIs)**: 1-3s response, auto-cached to L1 & L2

**Cost Savings**: 80-90% reduction in API costs through intelligent caching strategy

**Database Capacity**: Supports up to 1M cached POIs with automatic LRU eviction at 95% capacity

**Query Optimization**: PostGIS geospatial indexes, trigram text search, and composite indexes on popular query patterns

This project demonstrates advanced full-stack development skills, production-grade caching architecture, real-time collaboration implementation, complex algorithm integration, cost optimization engineering, and the creation of a scalable application that solves real-world coordination challenges with sophisticated user experience design and enterprise-ready infrastructure.`,
    image: "/images/projects/midpoint-maps.png",
    technologies: ["Next.js", "React", "Supabase", "PostgreSQL", "PostGIS", "Redis", "Tailwind CSS", "Leaflet", "OpenRouteService", "WebSocket"],
    categories: ["Web & Software Development", "Real-time Collaboration", "Performance Engineering"],
    demoLink: "https://getmidpoint.com/",
  },
  {
id: "sapot-platform",
title: "SAPOT – Peer Support Mental Health Platform",
description: "A modernized mental health support marketplace rebuilt for performance, scalability, and future expansion using Vercel and Supabase, featuring intelligent booking, real-time availability, and integrated payments.",
fullDescription: `
## Project Overview

SAPOT is a Malaysian peer support platform connecting individuals seeking mental health support with trained peer supporters who have lived experience. I was engaged by the SAPOT team to rebuild and modernize their existing system — originally run on WordPress — into a fully custom, scalable full-stack platform hosted on **Vercel** and powered by **Supabase**.  

The redevelopment included migrating data and workflows to a modern tech stack, introducing real-time booking, secure authentication, integrated payments, automated calendar scheduling, and an extensible admin system to support future growth and product features.

## Key Features

🔐 **Authentication & Access Control**: Implemented secure Supabase authentication with user roles (client/supporter/admin), email login, and profile management with picture uploads.

🎯 **Intelligent Booking System**: Developed dual booking workflows—searching by peer supporters’ availability or by lived experience—with real-time schedule visualization and conflict prevention.

👥 **Supporter Matching**: Added advanced filtering for lived experiences (e.g., Depression, Anxiety, Trauma), community groups (Students, Parents, Caregivers), spoken languages, and certifications.

🗳️ **Availability Management**: Built a hybrid scheduling system that supports recurring availability, one-off exceptions, and automatic conflict detection.

💬 **Session Support**: Integrated both online sessions and in-person appointments, including dynamic pricing by supporter level.

📍 **Payment Gateway Integration**: Integrated **Billplz** (for Malaysian payments) with secure webhook verification, payment tracking, and Stripe-readiness for international expansion.

📊 **Supporter Dashboard**: Implemented management tools for session tracking, availability editing, profile updates, and calendar synchronization.

🛡️ **Admin Dashboard**: Created admin interfaces for managing users, bookings, services, and payments, with full audit logging and role-based access.

⚡ **Performance & Scalability**: Optimized data fetching with caching and pagination, added image compression via Sharp, and implemented efficient database queries and RLS security.

## Technical Highlights

✔ **Full-Stack Architecture**: Rebuilt using Next.js 15 (App Router) with React 19 Server Components, TypeScript, and serverless API routes on Vercel  

✔ **Real-time Backend**: Supabase PostgreSQL with Row-Level Security, JWT authentication, and real-time subscriptions  

✔ **Payment Integration**: Custom Billplz SDK with X-Signature webhook verification, sandbox/production modes, and Stripe integration prep  

✔ **Image Optimization**: Sharp server-side compression with dual-resolution storage and WebP conversion  

✔ **Admin Tools**: Comprehensive admin audit logging, error tracking, and secure service-role architecture  

✔ **Security**: Row-Level Security, separated service roles, webhook verification, and robust session management  

## Technologies Used

🔹 **Frontend**: Next.js 15 | React 19 | TypeScript 5 | Tailwind CSS 4 | Framer Motion | Phosphor Icons  
🔹 **Backend**: Supabase | PostgreSQL | Zod Validation | Next.js API Routes | Vercel Serverless Functions  
🔹 **External APIs**: Billplz | Google Calendar API | Google Meet | Sharp Image Processing  
🔹 **Real-time**: Supabase Realtime | Server-Sent Events | Caching Layer with TTL  
🔹 **Tools**: Date-fns | Image Compression | Role-Based Access | Row-Level Security  

This project showcases my ability to **rebuild and future-proof** an existing platform by migrating it to a modern, scalable, and secure full-stack architecture. The result is a production-ready healthcare platform that maintains SAPOT’s mission while enhancing performance, maintainability, and room for growth.`,
image: "/images/projects/sapot-platform.png",
technologies: ["Next.js", "React", "TypeScript", "Supabase", "PostgreSQL", "Tailwind CSS", "Billplz", "Google Calendar API", "Sharp"],
categories: ["Web & Software Development", "Healthcare Technology"],
demoLink: "https://book.sapot.app",
  },
  {
    id: "saadah-soulmates",
    title: "SaadahSoulmates – Matchmaking Web Application",
    description: "A modern, responsive matchmaking platform built with React, TypeScript, and Supabase, featuring profile browsing, match scoring, and user authentication.",
    fullDescription: `
## Project Overview

Developed SaadahSoulmates, a modern and responsive matchmaking web application designed to connect individuals within a specific community context. The platform facilitates user registration, profile creation, browsing potential matches with calculated compatibility scores, and viewing detailed profiles. It includes both a full experience for authenticated users and a demo mode for guests.

## Key Features

🔐 **Secure Authentication & Session Management**: Supabase Auth with persistent sessions and profile completion tracking

👤 **Comprehensive Profile System**:
- Detailed profile creation with 15+ data points (religious level, education, interests, languages, marital status, etc.)
- Real-time inline form validation with auto-scroll to errors
- Wali (guardian) contact information fields
- Privacy controls and profile editing

🎯 **Smart Matching & Discovery**:
- Advanced compatibility scoring algorithm
- Multi-factor filtering (gender, location, religious level, marital status, education)
- Fuzzy search with Fuse.js for flexible name/location matching
- Dynamic sorting options (newest, oldest, compatibility)

🔗 **Connection Management**:
- Request/accept/reject connection flow with status tracking
- Real-time toast notifications for connection actions
- Block/unblock functionality for safety

🎨 **Modern UI/UX**:
- Glassmorphism design language throughout
- Responsive layout (desktop, tablet, mobile)
- Background image slider with overlay effects
- Scroll-triggered fade-in animations
- React Hot Toast for notifications
- Loading states and skeleton screens

🎭 **Guest Experience**:
- Demo mode with sample profiles for unauthenticated users
- Public About page explaining platform mission

## Technical Highlights

✔ Migrated from Create React App to Vite 7.1 for 20x faster dev server (423ms vs 5-10s)

✔ Built with React 18.3 and TypeScript 5.9 for type safety and modern features

✔ Supabase backend integration with PostgreSQL and Row Level Security

✔ React Router 6.4 for client-side routing with protected routes

✔ React Context API for global state management (auth, profile, session)

✔ Custom inline form validation replacing blocking alert() popups

✔ React Hot Toast integration with custom styling

✔ Fuse.js fuzzy search for flexible profile discovery

✔ Automatic profile completion redirects for incomplete registrations

✔ Optimized build with code splitting (vendor, Supabase, main bundles)

✔ Custom CSS with glassmorphism effects, gradient overlays, and smooth transitions

## Technologies Used

🔹 **Frontend**: React 18.3 | TypeScript 5.9 | Vite 7.1 | React Router 6.4 | React Hot Toast

🔹 **Backend**: Supabase (Auth + PostgreSQL)

🔹 **State Management**: React Context API

🔹 **Styling**: Custom CSS (Glassmorphism) | Responsive Design

🔹 **Search**: Fuse.js (fuzzy matching)

🔹 **Build Tools**: Vite | TypeScript Compiler

This project demonstrates advanced full-stack development skills, modern React patterns, authentication implementation, complex algorithm integration, and the creation of a culturally-sensitive application that bridges traditional values with contemporary technology.`,
    image: "/images/projects/saadah-soulmates.png",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "React Router", "React Context API", "React Hot Toast", "Fuse.js"],
    categories: ["Web & Software Development"],
    demoLink: "https://saadah-matchmaking.vercel.app/",
    // repoLink: "#",
  },
  {
    id: "little-lemon",
    title: "Little Lemon Restaurant",
    description: "A stylish and responsive React-based website for Little Lemon Restaurant, enabling users to browse the menu and make table reservations. Developed as a capstone project for a front-end development course.",
    fullDescription: `
## Project Overview

Little Lemon is a capstone project for a front-end development course, resulting in a stylish and responsive website for a fictional restaurant. The application allows users to seamlessly explore the restaurant's culinary offerings and make table reservations through an interactive interface. It's built with React to provide a dynamic user experience.

## Key Features

*   **Intuitive Navigation:** Easily navigate through various sections of the website.
*   **Dynamic Menu Display:** Explore diverse food and drink items in an engaging format.
*   **Interactive Reservation Form:** Book tables effortlessly.
*   **Instant Booking Confirmation:** Receive immediate feedback upon successful reservation.
*   **Modern User Interface:** Crafted with React for a responsive and visually appealing experience.
*   **Contact Integration:** Provides simple ways to get in touch with the restaurant.

## Key Contributions & Technical Highlights

✔ Developed the frontend application using React and JavaScript, focusing on a clean and user-friendly interface.

✔ Implemented core functionalities such as menu display and a table reservation system.

✔ Ensured the website is responsive, adapting to various screen sizes for optimal viewing on desktop and mobile devices.

✔ Focused on creating a modern and engaging user experience as part of a capstone project.

## Technologies Used

🔹 React | JavaScript | CSS | HTML

This project showcases the ability to build a functional and aesthetically pleasing web application for a restaurant business using modern front-end technologies.
`,
    image: "/images/projects/little-lemon-preview.png",
    technologies: ["React", "JavaScript", "CSS", "HTML"],
    categories: ["Web & Software Development"],
    demoLink: "https://little-lemon-rust.vercel.app/",
    githubLink: "https://github.com/TheIronA/little-lemon",
  },
  {
    id: "nec-diagnosis",
    title: "NEC Diagnosis in Neonates",
    description: "This project explores the application of deep learning for the early diagnosis of Necrotizing Enterocolitis (NEC), a life-threatening gastrointestinal condition in premature infants. Given the challenges of diagnosing NEC through conventional methods, this study leverages AI to assist clinicians in detecting NEC from X-ray imaging data.",
    fullDescription: `
## Project Overview

This project explores the application of deep learning for the early diagnosis of Necrotizing Enterocolitis (NEC), a life-threatening gastrointestinal condition in premature infants. Given the challenges of diagnosing NEC through conventional methods, this study leverages AI to assist clinicians in detecting NEC from X-ray imaging data.

## Key Achievements

- Developed a deep learning model using SE-ResNet50, fine-tuned on NEC X-ray datasets.
- Addressed the challenge of limited data (388 images) through data augmentation and transfer learning from ImageNet and CheXpert datasets.
- Investigated the impact of hyperparameter tuning, image transforms, and different CNN architectures on model performance.
- Achieved an 87.5% accuracy, outperforming other tested models.

## Key Contributions

✔ Implemented transfer learning to enhance NEC detection accuracy.

✔ Applied data augmentation techniques to improve model generalization.

✔ Conducted comparative analysis of ResNet, DenseNet, EfficientNet, and other architectures.

✔ Fine-tuned model hyperparameters to optimize performance.

✔ Designed a robust workflow for automated NEC diagnosis from medical imaging data.

## Technologies Used

🔹 Python | PyTorch | CUDA | Pandas | NumPy

This AI-driven approach has demonstrated strong potential in aiding early NEC diagnosis, paving the way for improved clinical decision-making and neonatal care.`,
    image: "/images/projects/nec-diagnosis.jpg",
    technologies: ["Python", "PyTorch", "CUDA", "Pandas", "NumPy"],
    categories: ["Machine Learning & AI"],
    dissertationLink: "/documents/nec-diagnosis-dissertation.pdf"
  },
  {
    id: "korean-learning",
    title: "Annyeong Aisyasya – Korean Language Learning Platform",
    description: "A modern, high-performance React web application for Korean language education, featuring secure course purchases, student management, and optimized media delivery.",
    fullDescription: `
## Project Overview

Annyeong Aisyasya is a comprehensive Korean language learning platform built for educator Aisyasya to showcase courses, manage students, and deliver educational resources. The application features a complete e-commerce workflow with Stripe integration, automated student registration tracking, optimized media delivery through a custom Drive proxy, and extensive performance optimizations for smooth user experience across devices.

## Key Features

💳 **Secure Payment Processing**: Full Stripe integration with webhook handling, payment confirmation flows, and secure downloadable product delivery guarded behind payment verification.

📊 **Student Management System**: Server-side Google Sheets integration automatically persists registration and purchase records, enabling efficient student tracking and course administration.

📚 **Course Showcase**: Dynamic course catalog with detailed descriptions, pricing information, and engaging visual presentation of learning materials.

🎬 **Optimized Media Delivery**: Custom Drive image proxy API serves portfolio images same-origin, with performance-optimized gallery preloading and client-requested autoplay for intro videos.

📱 **Responsive Design**: Mobile-first, accessible UI that adapts seamlessly across devices with modern CSS and thoughtful layout considerations.

🔔 **Promotion System**: Built-in announcement modal for highlighting special offers, new courses, and important updates to students.

📈 **Analytics Integration**: Google Analytics 4 (GA4) page tracking for monitoring user engagement and optimizing course offerings.

## Technical Highlights

✔ **Modern React Architecture**: Route-based code splitting using React.lazy and Suspense for optimal bundle sizes and faster initial load times.

✔ **Full-Stack Integration**: Express API routes for backend operations, including payment webhooks, image proxying, and spreadsheet updates.

✔ **Payment Security**: Stripe webhook-safe handling ensures reliable payment processing and prevents unauthorized access to paid content.

✔ **Performance Optimizations**:
  - Reduced decorative DOM and animation overhead
  - GPU-friendly CSS transformations
  - Lazy-loading strategies for images and routes
  - Optimized asset loading sequences
  - Image placeholders for progressive loading

✔ **Vercel Deployment**: Serverless-friendly configuration optimized for Vercel's edge network and deployment pipeline.

✔ **Modular Component Design**: Reusable, maintainable components following React best practices for scalability.

## Technologies Used

🔹 **Frontend**: React | React Router | Modern CSS | JavaScript

🔹 **Backend**: Express | Node.js | Serverless Functions

🔹 **Payments**: Stripe API | Webhook Integration

🔹 **Data Management**: Google Sheets API (google-spreadsheet)

🔹 **Analytics**: Google Analytics 4 (GA4)

🔹 **Deployment**: Vercel | Edge Functions

🔹 **Performance**: React.lazy | Suspense | GPU-accelerated CSS

This project demonstrates full-stack e-commerce implementation, third-party API integration, performance optimization strategies, and the creation of a production-ready educational platform that balances rich media content with fast load times.`,
    image: "/images/projects/korean-learning.png",
    technologies: ["React", "React Router", "Stripe", "Google Sheets API", "Express", "Node.js", "Vercel", "CSS", "JavaScript"],
    demoLink: "https://learnkoreanwithaisyasya.com",
    categories: ["Web & Software Development", "E-commerce"],
  },
  {
    id: "london-tube-planner",
    title: "LondonTube – Journey Planning Python Package",
    description: "A Python package for planning optimal London Underground journeys, integrating live service disruption updates using Python, requests, and a web service API.",
    fullDescription: `
## Project Overview

Developed LondonTube, a pip-installable Python package for planning efficient journeys across the London Underground, accounting for real-time disruptions. The platform features a command-line interface for route planning and a versatile Network class for graph analysis, completed collaboratively with modern development practices.

## Key Features

*   **Journey Planner:** User-friendly CLI for fastest routes, supporting station names/indices and optional dates.
*   **Live Disruptions:** Queries web service for daily updates, dynamically adjusting routes.
*   **Network Analysis:** Implements Dijkstra's algorithm, n-distant neighbors, and network combination.
*   **Visualization:** Optional PNG route plots with all Underground stations.
*   **Robustness:** Validates inputs, handles errors (e.g., no internet, invalid stations).
*   **Documentation:** Sphinx-based guides, tutorials, and doctests.

## Key Contributions & Technical Highlights

✔ Built Network class for node/adjacency matrix operations and optimized pathfinding.

✔ Enhanced distant_neighbours performance, benchmarked with detailed reports/plots.

✔ Used pytest for unit/mock tests, GitHub Actions for CI, and Git for version control.

✔ Packaged with metadata, ensuring easy installation and dependency management.

## Technologies Used

🔹 Python | requests | pytest | matplotlib | Sphinx | Git | GitHub

This project showcases efficient network algorithms, real-time data integration, and collaborative development for a scalable journey planning solution.`,
    image: "/images/projects/london-tube.jpg",
    technologies: ["Python", "requests", "pytest", "matplotlib", "Sphinx", "Git", "GitHub"],
    categories: ["Web & Software Development"],
  },
  {
    id: "smart-glasses",
    title: "AI-Powered Smart Glasses for the Visually Impaired",
    description: "AI-driven smart glasses providing real-time object detection and text-to-speech capabilities.",
    fullDescription: `
A Low-Cost, High-Performance Assistive Device

## Project Overview

This project designs and develops smart glasses to enhance independence for visually impaired individuals. The glasses leverage AI for real-time object recognition, optical character recognition (OCR), and audio feedback, enabling users to navigate their surroundings and interact with everyday objects more effectively.

## Key Features

✔ Object Recognition: Identifies common household and grocery items using the YOLOv8-tiny model and Google Gemini API for high accuracy.

✔ Text-to-Speech: Reads printed text (e.g., labels, signs) aloud using advanced OCR technology.

✔ User-Friendly Design: Lightweight (233g), ergonomic frame, and intuitive single-button operation.

✔ Affordable: Built with cost-effective components (total budget: £500) to ensure accessibility.

✔ Long Battery Life: 2500mAh power bank supports up to 6 hours of continuous use.

## Technical Highlights

Hardware: Raspberry Pi 4 + 8MP camera for high-resolution image capture.

Software:
  - Object Detection: Fine-tuned YOLOv8-tiny model trained on grocery datasets (83.3% accuracy).
  - OCR: Google Gemini API for multilingual text recognition, outperforming traditional tools like Pytesseract.
  - Audio: 3.5mm jack for clear, private audio feedback.

3D-Printed Case: PLA material optimized for durability and comfort via Finite Element Analysis (FEA).

## Testing & Results

- Live Testing: 100% accuracy in identifying objects at 25–100 cm distances (e.g., yogurt tubs, water bottles).
- User Feedback: Praised for simplicity and responsiveness; noted improvements needed in weight distribution and camera alignment.
- FEA Analysis: PLA selected for optimal stress resistance (0.033 MPa) and minimal deformation (2.787 mm).

## Future Work

- Hardware: Lighter components (Raspberry Pi Pico), dual-lens camera, and waterproofing.
- Software: Voice commands, facial recognition, and expanded language support.
- Market Strategy: Targeting 1.5% of the UK's 340,000 registered visually impaired users, with a £299 price point (vs. competitors' £1,500+).

## Why This Project Matters

- Impact: Over 2 million people in the UK live with visual impairments, often facing limited access to affordable assistive tools.
- Innovation: Combines cutting-edge AI with practical, user-centric design.
- Sustainability: Recyclable materials and energy-efficient components.`,
    image: "/images/projects/smart-glasses.jpg",
    technologies: ["Python", "OpenCV", "NLP", "Raspberry Pi", "TensorFlow"],
    categories: ["Machine Learning & AI", "Assistive Technology"],
  },
  {
    id: "artintel",
    title: "ArtIntel – AI-Powered Artwork Pricing",
    description: "Multi-modal AI model for estimating artwork prices using historical trends and market analysis.",
    fullDescription: `
## Project Overview

ArtIntel is an innovative AI platform that analyzes artwork pricing using deep learning and natural language processing. The system combines image recognition and market trend analysis to estimate artwork prices.

## Key Contributions

✔ Developed a multi-modal model using ResNet (image processing) and BERT (text analysis).

✔ Implemented similarity search with cosine similarity to recommend similar artworks.

✔ Scraped large-scale artwork data for automated metadata extraction.

## Technical Challenges

✔ Handling diverse art styles and mediums in the dataset.

✔ Integrating AI-powered insights into a user-friendly interface.

✔ Optimizing price estimation accuracy based on historical trends.

## Technologies Used

🔹 Python | React | AI Models | Web Scraping`,
    image: "/images/projects/artintel.jpg",
    technologies: ["React", "AI Models", "Web Scraping"],
    categories: ["Machine Learning & AI", "Web & Software Development"],
  },
  {
    id: "mouse-replacement-emg",
    title: "Mouse Replacement Using EMG Signals",
    description: "An assistive technology enabling computer cursor control via forearm muscle signals, built with Arduino, EMG sensors, and C++ for users with mobility impairments.",
    fullDescription: `
## Project Overview

Developed an innovative EMG-based interface to control a computer mouse using muscle signals from the forearm. This assistive technology interprets muscle contractions to enable real-time cursor navigation, offering a solution for individuals with limited mobility. The project involved designing a hardware circuit, programming an Arduino-based control system, and creating a responsive prototype.

## Key Features

*   **EMG Signal Processing:** Filters and processes forearm muscle signals for accurate detection.
*   **Cursor Control:** Maps muscle contractions to cursor movements (up, down, left, right) and left-click actions.
*   **Real-Time Response:** Low-latency system for smooth navigation with adjustable movement speed.
*   **User Calibration:** Threshold-based detection tailored to varying muscle signal strengths.
*   **Switch Activation:** Toggle switch to enable/disable mouse control for user convenience.

## Key Contributions & Technical Highlights

✔ Designed and built a circuit to amplify and filter EMG signals, minimizing noise and false positives.

✔ Programmed an Arduino to read analog inputs from EMG sensors, translating signals into mouse commands using the Mouse.h library.

✔ Implemented threshold-based logic (>655–900 range) for directional and click actions, with delays (7–200ms) to optimize responsiveness.

✔ Addressed challenges in signal consistency by fine-tuning analog read thresholds and averaging techniques (commented for future optimization).

✔ Optimized code for minimal latency, balancing speed and accuracy in cursor movements.

## Technical Challenges

✔ Reducing signal noise to prevent unintended cursor movements.

✔ Calibrating thresholds for diverse muscle activation patterns across users.

✔ Achieving low-latency performance within Arduino's processing constraints.

## Technologies Used

🔹 Arduino | EMG Sensors | Signal Processing | C++ | Mouse.h Library

This project demonstrates a practical assistive technology, combining embedded systems, signal processing, and user-focused design to enhance accessibility.`,
    image: "/images/projects/mouse-replacement.jpg",
    technologies: ["Arduino", "EMG Sensors", "Signal Processing", "C++", "Mouse.h Library"],
    categories: ["Embedded Systems"],
  },
];

export default projects; 